import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Box,
  IconButton,
  AppBar,
  Toolbar,
  makeStyles,
  useMediaQuery,
  useTheme,
  Tooltip
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import AssignmentIcon from '@material-ui/icons/Assignment';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import PeopleIcon from '@material-ui/icons/People';
import { authenticationService } from '../services';
import logo from '../assets/images/logo.png';
import logoReducido from '../assets/images/logo_reducido.png';

const drawerWidthExpanded = 260;
const drawerWidthCollapsed = 70;

const useStyles = makeStyles((theme) => ({
  drawer: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerExpanded: {
    width: drawerWidthExpanded,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerCollapsed: {
    width: drawerWidthCollapsed,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
  },
  drawerPaper: {
    backgroundColor: '#f5f5f5',
    borderRight: '1px solid #e0e0e0',
  },
  drawerPaperExpanded: {
    width: drawerWidthExpanded,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperCollapsed: {
    width: drawerWidthCollapsed,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
  },
  drawerContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  logoContainer: {
    padding: '0.5rem 1rem',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '56px',
    height: '56px',
  },
  logoContainerCollapsed: {
    padding: '0.5rem',
  },
  menuList: {
    flexGrow: 1,
    paddingTop: '1rem',
  },
  menuItem: {
    margin: '0.25rem 0.5rem',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#e3f2fd',
    },
  },
  menuItemCollapsed: {
    margin: '0.25rem auto',
    justifyContent: 'center',
    width: '50px',
    minHeight: '50px',
    padding: '8px',
  },
  menuItemSelected: {
    backgroundColor: 'var(--main-green) !important',
    color: '#fff !important',
    '&:hover': {
      backgroundColor: 'var(--light-blue) !important',
    },
    '& $menuItemIcon': {
      color: '#fff !important',
    },
  },
  menuItemIcon: {
    minWidth: '40px',
    color: 'rgba(0, 0, 0, 0.54)',
  },
  menuItemIconCollapsed: {
    minWidth: 'unset',
    marginRight: 0,
  },
  menuItemIconSelected: {
    color: '#fff !important',
  },
  logoutContainer: {
    padding: '1rem',
    borderTop: '1px solid #e0e0e0',
  },
  logoutContainerCollapsed: {
    padding: '0.5rem',
    display: 'flex',
    justifyContent: 'center',
  },
  logoutButton: {
    backgroundColor: '#dc3545',
    color: '#fff',
    textTransform: 'none',
    fontWeight: 600,
    padding: '0.75rem',
    '&:hover': {
      backgroundColor: '#c82333',
    },
  },
  logoutButtonCollapsed: {
    minWidth: '50px',
    width: '50px',
    height: '50px',
    borderRadius: '8px',
    padding: 0,
  },
  mobileAppBar: {
    backgroundColor: '#fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderBottom: '1px solid #e0e0e0',
  },
  mobileToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mobileLogo: {
    height: '40px',
    width: 'auto',
  },
}));

const Sidebar = ({ onLogout }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const menuItems = [
    {
      text: 'Solicitudes',
      icon: <AssignmentIcon />,
      path: '/solicitudes'
    },
    {
      text: 'Productores',
      icon: <PeopleIcon />,
      path: '/productores'
    }
  ];

  const handleLogout = async () => {
    try {
      await authenticationService.logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      if (onLogout) {
        onLogout();
      }
      history.push('/login');
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuItemClick = (path) => {
    history.push(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsHovering(false);
    }
  };

  // El menú se expande solo cuando hay hover
  const isExpanded = isHovering;

  const drawerContent = (
    <Box 
      className={classes.drawerContainer}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Logo/Header */}
      <Box className={`${classes.logoContainer} ${!isExpanded && !isMobile ? classes.logoContainerCollapsed : ''}`}>
        {!isExpanded && !isMobile ? (
          <img
            src={logoReducido}
            alt="ST"
            style={{ height: '36px', width: 'auto', objectFit: 'contain' }}
          />
        ) : (
          <img
            src={logo}
            alt="ST Securities"
            style={{ height: '40px', width: 'auto' }}
          />
        )}
      </Box>

      {/* Menu Items */}
      <List className={classes.menuList}>
        {menuItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          const menuItemContent = (
            <ListItem
              button
              key={item.text}
              onClick={() => handleMenuItemClick(item.path)}
              selected={isActive}
              className={`
                ${classes.menuItem} 
                ${isActive ? classes.menuItemSelected : ''} 
                ${!isExpanded && !isMobile ? classes.menuItemCollapsed : ''}
              `}
            >
              <ListItemIcon
                className={`
                  ${classes.menuItemIcon} 
                  ${isActive ? classes.menuItemIconSelected : ''}
                  ${!isExpanded && !isMobile ? classes.menuItemIconCollapsed : ''}
                `}
              >
                {item.icon}
              </ListItemIcon>
              {(isExpanded || isMobile) && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    style: {
                      fontWeight: isActive ? 600 : 400,
                      fontSize: '14px'
                    }
                  }}
                />
              )}
            </ListItem>
          );

          // En modo colapsado (sin hover), envolver con Tooltip
          if (!isExpanded && !isMobile) {
            return (
              <Tooltip key={item.text} title={item.text} placement="right" arrow>
                {menuItemContent}
              </Tooltip>
            );
          }

          return menuItemContent;
        })}
      </List>

      {/* Logout Button */}
      <Box className={`${classes.logoutContainer} ${!isExpanded && !isMobile ? classes.logoutContainerCollapsed : ''}`}>
        {!isExpanded && !isMobile ? (
          <Tooltip title="Cerrar Sesión" placement="right" arrow>
            <IconButton
              onClick={handleLogout}
              className={`${classes.logoutButton} ${classes.logoutButtonCollapsed}`}
            >
              <ExitToAppIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            startIcon={<ExitToAppIcon />}
            onClick={handleLogout}
            className={classes.logoutButton}
          >
            Cerrar Sesión
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile AppBar */}
      {isMobile && (
        <AppBar position="fixed" className={classes.mobileAppBar}>
          <Toolbar className={classes.mobileToolbar}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              style={{ color: '#333' }}
            >
              <MenuIcon />
            </IconButton>
            <img
              src={logo}
              alt="ST Securities"
              className={classes.mobileLogo}
            />
            <Box style={{ width: '48px' }} /> {/* Spacer para centrar logo */}
          </Toolbar>
        </AppBar>
      )}

      {/* Drawer */}
      <Drawer
        className={`${classes.drawer} ${!isExpanded && !isMobile ? classes.drawerCollapsed : classes.drawerExpanded}`}
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        classes={{
          paper: `${classes.drawerPaper} ${!isExpanded && !isMobile ? classes.drawerPaperCollapsed : classes.drawerPaperExpanded}`,
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
